import { ArrowBackIcon } from '@chakra-ui/icons'
import { Form, Formik, useFormikContext } from 'formik'
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useGetCommodityOverrideConfigurables,
  useGetLocationConfigurables,
  useGetLocationConfigurations,
  useUpdateLocationConfiguration,
} from 'src/api/queries/microservices/platform-markets-location-config'
import ConfigPanel from 'src/companies/configurations/config-panel'
import { useCompanyContext } from 'src/companies/routes/company-context'
import ConfigurationPanelFields from 'src/components/configuration-panel/ConfigurationPanelFields'
import { Box, Button, Placeholders, Stack } from 'src/ui'
import { getEnvironment } from 'src/utils'
import { hasPermission } from 'src/utils/permissions'
import { flattenGroupedTenantConfigurations } from 'src/utils/tenant-configuration'
import { useToast } from 'src/utils/toast'
import PlatformLocationConfigPageCommodityOverridesSection from './platform-location-config-page-commodity-overrides-section'
import {
  buildPlatformLocationInitialValues,
  type PlatformLocationFormValues,
} from './platform-location-form'

function PlatformLocationConfigForm({
  canEdit,
  backPath,
  locationName,
  commodityConfigurables,
  datasourceProviderId,
}: {
  canEdit: boolean
  backPath: string
  locationName: string
  commodityConfigurables: TenantConfigResponse[]
  datasourceProviderId: string
}) {
  const navigate = useNavigate()
  const { values, isSubmitting, submitForm } = useFormikContext<PlatformLocationFormValues>()

  return (
    <Form>
      <ConfigPanel
        label={locationName}
        expanded
        hideToggleIcon
        editPermission="company_properties_edit"
        panelState="editing"
        formData={{ isSubmitting }}
        saveIsDisabled={!canEdit}
        onCancelEdit={() => navigate(backPath)}
        onSave={submitForm}
      >
        <ConfigurationPanelFields
          formFields={values.locationFields}
          fieldPathPrefix="locationFields"
        />
        <PlatformLocationConfigPageCommodityOverridesSection
          canEdit={canEdit}
          commodityConfigurables={commodityConfigurables}
          datasourceProviderId={datasourceProviderId}
        />
      </ConfigPanel>
    </Form>
  )
}

export default function PlatformLocationConfigPage() {
  const { company } = useCompanyContext()
  const toast = useToast()
  const navigate = useNavigate()
  const canEdit = hasPermission('company_properties_edit')
  const { id: locationId } = useParams()

  const locationConfigurablesQuery = useGetLocationConfigurables()
  const commodityConfigurablesQuery = useGetCommodityOverrideConfigurables()
  const locationConfigurationsQuery = useGetLocationConfigurations()
  const { mutateAsync: updateLocationConfiguration } = useUpdateLocationConfiguration()

  const locationConfiguration =
    locationConfigurationsQuery.data?.find((location) => location.locationId === locationId) ?? null

  const initialValues = buildPlatformLocationInitialValues(
    locationConfiguration,
    locationConfigurablesQuery.data,
    commodityConfigurablesQuery.data
  )

  const backPath = `/companies/${getEnvironment()}/${company.slug}/markets/#Platform%20Locations`
  const queries = [
    locationConfigurablesQuery,
    commodityConfigurablesQuery,
    locationConfigurationsQuery,
  ]

  if (queries.some((query) => query.status === 'pending')) {
    return <Placeholders.LoadingState mt={8} />
  }

  if (queries.some((query) => query.status === 'error')) {
    return <Placeholders.FailedState mt={8} />
  }

  if (!locationConfiguration || !initialValues) {
    return <Placeholders.FailedState mt={8} message="Platform location configuration not found" />
  }

  return (
    <Stack spacing={4} alignItems="start" width="100%">
      <Button
        variant="link"
        colorScheme="primary"
        leftIcon={<ArrowBackIcon />}
        onClick={() => navigate(backPath)}
      >
        Back
      </Button>

      <Box w="100%">
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validateOnChange={false}
          onSubmit={async (values) => {
            if (!canEdit) return
            try {
              await updateLocationConfiguration({
                locationId: locationConfiguration.locationId,
                configurations: flattenGroupedTenantConfigurations(values.locationFields),
                commodityOverrides: values.commodityOverrides.map((override) => ({
                  commodityId: override.commodityId,
                  configurations: flattenGroupedTenantConfigurations(override.fields),
                })),
              })

              toast({ title: 'Success', description: 'Updated platform location configuration' })
              navigate(backPath)
            } catch {
              toast({
                status: 'error',
                title: 'Error',
                description: 'Failed to update platform location configuration',
              })
            }
          }}
        >
          <PlatformLocationConfigForm
            canEdit={canEdit}
            backPath={backPath}
            locationName={locationConfiguration.locationName}
            commodityConfigurables={commodityConfigurablesQuery.data ?? []}
            datasourceProviderId={locationConfiguration.datasourceProviderId}
          />
        </Formik>
      </Box>
    </Stack>
  )
}
